import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import {
  ArrowLeft, Globe, ChevronDown, Package, FileText, Users, CreditCard,
  Container, Scale, Ship, Plane, MessageCircle, BookOpen,
} from "lucide-react";

const KnowledgeCenterPage = () => {
  const { lang, toggleLang } = useLang();
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = lang === "es"
      ? "Aprende sobre Comercio Internacional | OKontainers"
      : "Learn About International Trade | OKontainers";
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", "https://okontainers.com/aprende");
  }, [lang]);

  const toggle = (i) => setOpenSection(openSection === i ? null : i);

  const sections = lang === "es" ? sectionsES : sectionsEN;
  const t = lang === "es" ? textsES : textsEN;

  return (
    <div className="bg-white min-h-screen" data-testid="knowledge-center-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-glass-scrolled">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2 shrink-0">
            <img src="/logo.png" alt="OKontainers" className="h-[86px] md:h-[108px] w-auto object-contain" />
          </a>
          <div className="flex items-center gap-3">
            <button onClick={toggleLang} data-testid="knowledge-lang-toggle"
              className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-navy transition-colors px-3 py-1.5 rounded-full border border-slate-200">
              <Globe size={14} />{lang === "es" ? "EN" : "ES"}
            </button>
            <a href="https://wa.me/34672808030" target="_blank" rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-2 bg-electric text-white text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-electric-hover transition-all shadow-lg shadow-electric/20">
              <MessageCircle size={16} />WhatsApp
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-navy pt-24 pb-16 md:pt-28 md:pb-20">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <a href="/" className="inline-flex items-center gap-2 text-electric-light hover:text-white text-sm font-medium mb-8 font-body transition-colors">
            <ArrowLeft size={16} />{t.back}
          </a>
          <div className="flex items-center gap-3 mb-4">
            <BookOpen size={24} className="text-electric-light" />
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-electric-light font-body">{t.overline}</p>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white font-heading mb-4">{t.headline}</h1>
          <p className="text-lg text-slate-300 font-body max-w-2xl">{t.subheadline}</p>
        </div>
      </section>

      {/* Hero image */}
      <div className="w-full h-[200px] md:h-[300px]">
        <img src="/containers-colorful.jpg" alt="Shipping containers" className="w-full h-full object-cover" />
      </div>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="space-y-4">
            {sections.map((section, i) => (
              <div key={i} data-testid={`knowledge-section-${i}`}
                className={`rounded-2xl border transition-all duration-300 ${openSection === i ? "bg-white border-electric/20 shadow-md" : "bg-white border-slate-200 hover:border-slate-300"}`}>
                <button onClick={() => toggle(i)} data-testid={`knowledge-toggle-${i}`}
                  className="w-full flex items-center justify-between gap-4 px-6 md:px-8 py-6 text-left">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${openSection === i ? "bg-electric/10" : "bg-slate-100"}`}>
                      <section.icon size={20} className={openSection === i ? "text-electric" : "text-slate-400"} />
                    </div>
                    <div>
                      <h3 className="font-bold text-navy text-base md:text-lg font-heading">{section.title}</h3>
                      <p className="text-slate-400 text-xs font-body mt-0.5">{section.subtitle}</p>
                    </div>
                  </div>
                  <ChevronDown size={20} className={`shrink-0 text-slate-400 transition-transform duration-300 ${openSection === i ? "rotate-180 text-electric" : ""}`} />
                </button>
                <AnimatePresence>
                  {openSection === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <div className="px-6 md:px-8 pb-8 pt-2">
                        {section.content}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 text-center bg-navy rounded-3xl p-10">
            <h3 className="text-2xl font-bold text-white font-heading mb-3">{t.ctaTitle}</h3>
            <p className="text-slate-300 font-body mb-6">{t.ctaSubtitle}</p>
            <a href="https://wa.me/34672808030" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-electric text-white font-semibold py-4 px-8 rounded-full hover:bg-electric-hover transition-all shadow-lg shadow-electric/30">
              <MessageCircle size={18} />{t.ctaButton}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy-dark py-10">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="OKontainers" className="h-8 w-auto brightness-0 invert" />
            <span className="text-slate-500 text-xs font-body">Copyright &copy; {new Date().getFullYear()} OKontainers | Powered by OKontainers</span>
          </div>
          <a href="/" className="text-slate-400 hover:text-white text-sm font-body transition-colors">&larr; {t.back}</a>
        </div>
      </footer>
    </div>
  );
};

/* ========== HELPER COMPONENTS ========== */
const Table = ({ headers, rows }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200">
    <table className="w-full text-sm">
      <thead><tr className="bg-slate-50">{headers.map((h, i) => <th key={i} className="text-left text-navy font-semibold px-4 py-3 font-heading">{h}</th>)}</tr></thead>
      <tbody>{rows.map((row, i) => <tr key={i} className="border-t border-slate-100">{row.map((cell, j) => <td key={j} className="px-4 py-3 text-slate-600 font-body">{cell}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

const InfoCard = ({ title, children }) => (
  <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
    <h4 className="font-bold text-navy text-sm mb-3 font-heading">{title}</h4>
    <div className="text-slate-600 text-sm leading-relaxed font-body space-y-2">{children}</div>
  </div>
);

/* ========== SPANISH CONTENT ========== */
const textsES = {
  back: "Volver al inicio", overline: "CENTRO DE CONOCIMIENTO",
  headline: "Aprende sobre Comercio Internacional",
  subheadline: "Todo lo que necesitas saber para importar con confianza. Guías prácticas escritas por nuestro equipo de expertos.",
  ctaTitle: "¿Tienes dudas sobre tu importación?",
  ctaSubtitle: "Nuestro equipo te asesora gratis. Sin compromiso.",
  ctaButton: "Consulta Gratis por WhatsApp",
};

const sectionsES = [
  {
    icon: Container, title: "Medidas y Capacidad de Contenedores", subtitle: "20’, 40’ y 40’HC: dimensiones, peso y europallets",
    content: (
      <div className="space-y-6">
        <p className="text-slate-600 text-sm font-body">Los tres tipos de contenedores más utilizados en importación marítima son el de 20 pies, 40 pies y 40 pies High Cube (HC). Aquí tienes las medidas exactas:</p>
        <Table headers={["", "20’ Standard", "40’ Standard", "40’ High Cube"]} rows={[
          ["Largo interior", "5,90 m", "12,03 m", "12,03 m"],
          ["Ancho interior", "2,35 m", "2,35 m", "2,35 m"],
          ["Alto interior", "2,39 m", "2,39 m", "2,69 m"],
          ["Volumen", "33,2 m³", "67,7 m³", "76,3 m³"],
          ["Peso máx. carga", "21.770 kg", "26.680 kg", "26.460 kg"],
          ["Europallets (80x120)", "11 pallets", "23-24 pallets", "23-24 pallets"],
        ]} />
        <InfoCard title="Europallets por contenedor">
          <p><strong>Contenedor 20’:</strong> Caben 11 europallets en una sola capa. Si apilas, depende de la altura de tu mercancía (máx. 2,39m de alto interior).</p>
          <p><strong>Contenedor 40’:</strong> Caben 23-24 europallets en una sola capa. Mismo ancho, el doble de largo.</p>
          <p><strong>Contenedor 40’HC:</strong> Misma capacidad de pallets pero 30cm más de altura. Ideal para mercancía voluminosa o pallets altos.</p>
        </InfoCard>
        <InfoCard title="¿Cuándo usar cada contenedor?">
          <p><strong>20’:</strong> Mercancía pesada (cerámica, metal, piedra) donde llegas al límite de peso antes que al de volumen.</p>
          <p><strong>40’:</strong> Mercancía estándar con buen equilibrio peso/volumen.</p>
          <p><strong>40’HC:</strong> Mercancía ligera y voluminosa (plásticos, textil, embalajes) donde necesitas espacio vertical extra.</p>
        </InfoCard>
      </div>
    ),
  },
  {
    icon: Scale, title: "Despacho de Aduanas: Qué Se Paga", subtitle: "Aranceles, IVA, tasas y costes del despacho",
    content: (
      <div className="space-y-6">
        <p className="text-slate-600 text-sm font-body">Al importar mercancía de fuera de la UE a España, se pagan estos conceptos en la aduana:</p>
        <Table headers={["Concepto", "Cálculo", "Observaciones"]} rows={[
          ["Arancel", "0% - 14% sobre valor CIF", "Varía según partida arancelaria (TARIC). Textil ~12%, electrónica ~0-3,7%, juguetes ~4,7%"],
          ["IVA importación", "21% sobre (valor CIF + arancel)", "Deducible en la declaración trimestral de IVA. IVA reducido 10% para alimentos"],
          ["Tasa T3 (AEAT)", "∼30-60€", "Tasa fija por inspección documental"],
          ["Honorarios agente", "∼80-200€", "Por despacho. Varía según complejidad"],
          ["Almacenaje (si aplica)", "Variable", "Si la mercancía queda retenida en puerto"],
        ]} />
        <InfoCard title="¿Qué es el valor CIF?">
          <p>CIF = Coste de la mercancía + Seguro + Flete. Es la base sobre la que se calculan aranceles e IVA. Por eso es importante que la factura comercial refleje correctamente el valor.</p>
        </InfoCard>
        <InfoCard title="Importante">
          <p>El <strong>IVA de importación es deducible</strong> al 100% en tu siguiente declaración trimestral. Esto significa que aunque lo pagas en aduana, lo recuperas. El arancel NO es deducible, es un coste real.</p>
        </InfoCard>
      </div>
    ),
  },
  {
    icon: Users, title: "Protagonistas de una Importación", subtitle: "Quién es quién en el proceso",
    content: (
      <div className="space-y-4">
        <p className="text-slate-600 text-sm font-body">Estos son los actores principales que intervienen en una importación internacional:</p>
        {[
          { role: "Importador (tú)", desc: "La empresa que compra la mercancía. Eres el responsable legal ante la aduana y el que paga aranceles e IVA." },
          { role: "Proveedor / Fábrica", desc: "El fabricante o vendedor en origen. Produce, embala y entrega la mercancía al transportista." },
          { role: "Transitario / Freight Forwarder", desc: "El organizador del transporte internacional. Coordina el envío desde origen hasta destino, negocia con navieras y gestiona la logística." },
          { role: "Naviera / Airline", desc: "La empresa de transporte que mueve físicamente la mercancía (barco, avión). Emite el B/L o AWB." },
          { role: "Agente de Aduanas", desc: "Profesional autorizado que tramita el DUA (Declaración Única Aduanera) ante la AEAT. Se asegura de que la documentación sea correcta." },
          { role: "OKontainers (nosotros)", desc: "Coordinamos TODO el proceso. Somos tu único punto de contacto: negociamos con el proveedor, coordinamos con el transitario, supervisamos la documentación y seguimos tu envío hasta la entrega." },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-4 bg-slate-50 rounded-xl p-4 border border-slate-100">
            <div className="shrink-0 w-8 h-8 rounded-full bg-electric/10 flex items-center justify-center text-electric font-bold text-xs font-heading">{i + 1}</div>
            <div>
              <h4 className="font-bold text-navy text-sm font-heading">{item.role}</h4>
              <p className="text-slate-500 text-sm font-body mt-1">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: Package, title: "LCL vs FCL: Diferencias", subtitle: "Cuándo usar carga completa o compartida",
    content: (
      <div className="space-y-6">
        <Table headers={["", "FCL (Full Container Load)", "LCL (Less than Container Load)"]} rows={[
          ["Qué es", "Contenedor completo solo para tu mercancía", "Compartes contenedor con otros importadores"],
          ["Coste", "Más económico por m³ en grandes volúmenes", "Más económico para envíos pequeños"],
          ["Tiempo", "Directo, sin consolidación", "Más lento (5-10 días extra por consolidación)"],
          ["Riesgo", "Menor (tu mercancía va sola)", "Mayor (manipulación al consolidar/desconsolidar)"],
          ["Volumen ideal", "A partir de 15-20 m³", "Menos de 15 m³"],
          ["Ejemplo", "30 pallets de producto terminado", "2-5 pallets o cajas sueltas"],
        ]} />
        <InfoCard title="Consejo OKontainers">
          <p>Si tu envío ocupa más del 50% de un contenedor, casi siempre es más rentable ir FCL. Nosotros te calculamos ambas opciones y te recomendamos la mejor.</p>
        </InfoCard>
      </div>
    ),
  },
  {
    icon: CreditCard, title: "Métodos de Pago Internacionales", subtitle: "Transferencia, carta de crédito, PayPal y más",
    content: (
      <div className="space-y-6">
        {[
          { method: "Transferencia bancaria (T/T)", desc: "El más común. Normalmente 30% anticipo al confirmar pedido + 70% antes del embarque o contra copia del B/L.", risk: "Medio" },
          { method: "Carta de crédito (L/C)", desc: "El banco del importador garantiza el pago al proveedor si cumple con las condiciones pactadas. Máxima seguridad para ambas partes.", risk: "Bajo" },
          { method: "PayPal / Alibaba Trade Assurance", desc: "Para pedidos pequeños o muestras. Comisiones altas (3-5%) pero con protección al comprador.", risk: "Bajo-Medio" },
          { method: "Open Account (pago a plazo)", desc: "Pagas 30-60-90 días después de recibir la mercancía. Solo con proveedores de mucha confianza y relación consolidada.", risk: "Alto para proveedor" },
        ].map((item, i) => (
          <div key={i} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-navy text-sm font-heading">{item.method}</h4>
              <span className="text-xs bg-electric/10 text-electric px-2 py-0.5 rounded-full font-body">Riesgo: {item.risk}</span>
            </div>
            <p className="text-slate-500 text-sm font-body">{item.desc}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: FileText, title: "Documentos Necesarios para Importar", subtitle: "B/L, factura comercial, packing list, certificados",
    content: (
      <div className="space-y-4">
        {[
          { doc: "Factura comercial (Commercial Invoice)", desc: "Emitida por el proveedor. Debe incluir: descripción detallada, cantidad, precio unitario, Incoterm, y datos de comprador/vendedor." },
          { doc: "Packing List", desc: "Detalle del embalaje: número de bultos, peso bruto y neto, dimensiones por bulto." },
          { doc: "Bill of Lading (B/L)", desc: "Documento de transporte marítimo emitido por la naviera. Acredita la propiedad de la mercancía." },
          { doc: "Air Waybill (AWB)", desc: "Equivalente al B/L pero para transporte aéreo." },
          { doc: "Certificado de origen", desc: "Acredita dónde se fabricó la mercancía. Puede reducir aranceles si hay acuerdo comercial." },
          { doc: "Certificado CE", desc: "Obligatorio para muchos productos vendidos en la UE: electrónica, juguetes, maquinaria, EPI, etc." },
          { doc: "DUA", desc: "Declaración Única Aduanera. Lo presenta el agente de aduanas. Resume toda la operación y determina los impuestos." },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-3 border-b border-slate-100 pb-3 last:border-0">
            <span className="text-electric font-bold text-xs font-heading mt-1 shrink-0">{i + 1}</span>
            <div>
              <h4 className="font-semibold text-navy text-sm font-heading">{item.doc}</h4>
              <p className="text-slate-500 text-xs font-body mt-0.5">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: Ship, title: "Incoterms: Quién Paga Qué", subtitle: "FOB, CIF, EXW, DDP y los más usados",
    content: (
      <div className="space-y-6">
        <p className="text-slate-600 text-sm font-body">Los Incoterms determinan quién paga el transporte, el seguro y en qué punto se transfiere el riesgo:</p>
        <Table headers={["Incoterm", "Significa", "Tú pagas", "Riesgo pasa a ti en"]} rows={[
          ["EXW", "Ex Works (en fábrica)", "Todo: recogida, transporte, seguro, aduana", "En la fábrica del proveedor"],
          ["FOB", "Free On Board", "Flete marítimo + seguro + aduana destino", "Cuando la mercancía está a bordo del barco"],
          ["CIF", "Cost, Insurance & Freight", "Solo aduana y transporte local en destino", "En el puerto de destino"],
          ["DDP", "Delivered Duty Paid", "Nada (todo incluido)", "En tu almacén (proveedor asume todo)"],
        ]} />
        <InfoCard title="Lo más habitual">
          <p><strong>FOB</strong> es el más utilizado en importaciones desde China. Tú (o tu transitario) controlas el flete y el seguro. <strong>EXW</strong> te da máximo control pero máxima responsabilidad. Nosotros te asesoramos sobre cuál conviene en cada caso.</p>
        </InfoCard>
      </div>
    ),
  },
];

/* ========== ENGLISH CONTENT ========== */
const textsEN = {
  back: "Back to home", overline: "KNOWLEDGE CENTER",
  headline: "Learn About International Trade",
  subheadline: "Everything you need to know to import with confidence. Practical guides written by our expert team.",
  ctaTitle: "Questions about your import?",
  ctaSubtitle: "Our team advises you for free. No commitment.",
  ctaButton: "Free Consultation via WhatsApp",
};

const sectionsEN = [
  {
    icon: Container, title: "Container Sizes & Capacity", subtitle: "20’, 40’ and 40’HC: dimensions, weight and europallets",
    content: (
      <div className="space-y-6">
        <p className="text-slate-600 text-sm font-body">The three most common container types for maritime import are 20ft, 40ft, and 40ft High Cube (HC):</p>
        <Table headers={["", "20’ Standard", "40’ Standard", "40’ High Cube"]} rows={[
          ["Internal length", "5.90 m", "12.03 m", "12.03 m"],
          ["Internal width", "2.35 m", "2.35 m", "2.35 m"],
          ["Internal height", "2.39 m", "2.39 m", "2.69 m"],
          ["Volume", "33.2 m³", "67.7 m³", "76.3 m³"],
          ["Max. cargo weight", "21,770 kg", "26,680 kg", "26,460 kg"],
          ["Europallets (80x120)", "11 pallets", "23-24 pallets", "23-24 pallets"],
        ]} />
        <InfoCard title="Europallets per container">
          <p><strong>20’ Container:</strong> Fits 11 europallets in a single layer. Stacking depends on your goods’ height (max 2.39m internal height).</p>
          <p><strong>40’ Container:</strong> Fits 23-24 europallets in a single layer. Same width, double the length.</p>
          <p><strong>40’HC:</strong> Same pallet capacity but 30cm more height. Ideal for bulky goods or tall pallets.</p>
        </InfoCard>
      </div>
    ),
  },
  {
    icon: Scale, title: "Customs Clearance: What You Pay", subtitle: "Duties, VAT, fees, and clearance costs",
    content: (
      <div className="space-y-6">
        <p className="text-slate-600 text-sm font-body">When importing goods from outside the EU into Spain, these are the costs at customs:</p>
        <Table headers={["Item", "Calculation", "Notes"]} rows={[
          ["Customs duty", "0% - 14% on CIF value", "Varies by tariff code (TARIC). Textiles ~12%, electronics ~0-3.7%, toys ~4.7%"],
          ["Import VAT", "21% on (CIF value + duty)", "Deductible in quarterly VAT return. Reduced 10% for food"],
          ["T3 Fee (AEAT)", "∼€30-60", "Fixed fee for document inspection"],
          ["Agent fees", "∼€80-200", "Per clearance. Varies by complexity"],
          ["Storage (if applicable)", "Variable", "If goods are held at port"],
        ]} />
        <InfoCard title="Important">
          <p><strong>Import VAT is 100% deductible</strong> in your next quarterly return. Customs duties are NOT deductible — they’re a real cost.</p>
        </InfoCard>
      </div>
    ),
  },
  {
    icon: Users, title: "Key Players in an Import", subtitle: "Who’s who in the process",
    content: (
      <div className="space-y-4">
        {[
          { role: "Importer (you)", desc: "The company purchasing goods. Legally responsible before customs and pays duties/VAT." },
          { role: "Supplier / Factory", desc: "The manufacturer or seller at origin. Produces, packs, and delivers goods to the carrier." },
          { role: "Freight Forwarder", desc: "Organizes international transport. Coordinates shipping, negotiates with carriers, manages logistics." },
          { role: "Shipping Line / Airline", desc: "Physically moves the goods (ship, plane). Issues the B/L or AWB." },
          { role: "Customs Agent", desc: "Licensed professional who files the customs declaration (DUA) with tax authorities." },
          { role: "OKontainers (us)", desc: "We coordinate EVERYTHING. Single point of contact: supplier negotiation, forwarder coordination, documentation, and tracking until delivery." },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-4 bg-slate-50 rounded-xl p-4 border border-slate-100">
            <div className="shrink-0 w-8 h-8 rounded-full bg-electric/10 flex items-center justify-center text-electric font-bold text-xs font-heading">{i + 1}</div>
            <div>
              <h4 className="font-bold text-navy text-sm font-heading">{item.role}</h4>
              <p className="text-slate-500 text-sm font-body mt-1">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: Package, title: "LCL vs FCL: Differences", subtitle: "When to use full container or shared load",
    content: (
      <div className="space-y-6">
        <Table headers={["", "FCL (Full Container Load)", "LCL (Less than Container Load)"]} rows={[
          ["What is it", "Full container just for your goods", "You share a container with other importers"],
          ["Cost", "Cheaper per m³ for large volumes", "Cheaper for small shipments"],
          ["Time", "Direct, no consolidation", "Slower (5-10 extra days for consolidation)"],
          ["Risk", "Lower (your goods travel alone)", "Higher (handling during consolidation)"],
          ["Ideal volume", "From 15-20 m³", "Less than 15 m³"],
        ]} />
      </div>
    ),
  },
  {
    icon: CreditCard, title: "International Payment Methods", subtitle: "Wire transfer, L/C, PayPal and more",
    content: (
      <div className="space-y-4">
        {[
          { method: "Wire Transfer (T/T)", desc: "Most common. Usually 30% deposit + 70% before shipping or against B/L copy.", risk: "Medium" },
          { method: "Letter of Credit (L/C)", desc: "Importer’s bank guarantees payment if supplier meets agreed conditions. Maximum security.", risk: "Low" },
          { method: "PayPal / Trade Assurance", desc: "For small orders or samples. High fees (3-5%) but buyer protection.", risk: "Low-Medium" },
          { method: "Open Account", desc: "Pay 30-60-90 days after receiving goods. Only with trusted, long-term suppliers.", risk: "High for supplier" },
        ].map((item, i) => (
          <div key={i} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-navy text-sm font-heading">{item.method}</h4>
              <span className="text-xs bg-electric/10 text-electric px-2 py-0.5 rounded-full font-body">Risk: {item.risk}</span>
            </div>
            <p className="text-slate-500 text-sm font-body">{item.desc}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: FileText, title: "Documents Needed for Importing", subtitle: "B/L, commercial invoice, packing list, certificates",
    content: (
      <div className="space-y-4">
        {[
          { doc: "Commercial Invoice", desc: "Issued by supplier. Must include: detailed description, quantity, unit price, Incoterm, buyer/seller details." },
          { doc: "Packing List", desc: "Packaging detail: number of packages, gross/net weight, dimensions per package." },
          { doc: "Bill of Lading (B/L)", desc: "Maritime transport document issued by the shipping line. Proves ownership of goods." },
          { doc: "Certificate of Origin", desc: "Certifies where goods were manufactured. Can reduce duties under trade agreements." },
          { doc: "CE Certificate", desc: "Mandatory for many EU products: electronics, toys, machinery, PPE, etc." },
          { doc: "DUA (Customs Declaration)", desc: "Filed by customs agent. Summarizes the operation and determines taxes." },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-3 border-b border-slate-100 pb-3 last:border-0">
            <span className="text-electric font-bold text-xs font-heading mt-1 shrink-0">{i + 1}</span>
            <div>
              <h4 className="font-semibold text-navy text-sm font-heading">{item.doc}</h4>
              <p className="text-slate-500 text-xs font-body mt-0.5">{item.desc}</p>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: Ship, title: "Incoterms: Who Pays What", subtitle: "FOB, CIF, EXW, DDP and the most used",
    content: (
      <div className="space-y-6">
        <Table headers={["Incoterm", "Means", "You pay", "Risk transfers at"]} rows={[
          ["EXW", "Ex Works", "Everything: pickup, transport, insurance, customs", "At supplier’s factory"],
          ["FOB", "Free On Board", "Sea freight + insurance + destination customs", "When goods are on board the ship"],
          ["CIF", "Cost, Insurance & Freight", "Only customs and local transport at destination", "At destination port"],
          ["DDP", "Delivered Duty Paid", "Nothing (all included)", "At your warehouse"],
        ]} />
      </div>
    ),
  },
];

export default KnowledgeCenterPage;
